import OT from "@opentok/client";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useCallback, useEffect, useRef, useState } from "react";

import { SidebarContent } from "components/Meeting/Live/VideoProvider";

import {
  LiveParticipantType,
  LiveRoomAccessDetails,
} from "store/meetings/types";
import { SingleVoting } from "store/voting/types";

import { addMessage } from "../../store/chat/reducer";
import { RetryChatMessage, SignalEvent } from "../../store/chat/types";
import { RootState } from "../../store/store";

const useSession = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [session, setSession] = useState<OT.Session | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [subscribers, setSubscribers] = useState<OT.Subscriber[]>([]);
  const [subscribersCount, setSubscribersCount] = useState<number>(0);
  const sessionRef = useRef<OT.Session | null>(null);
  const [sidebarContent, setSidebarContent] = useState<SidebarContent>(null);
  const [agendaOpened, setAgendaOpened] = useState(false);
  const [agendaSmall, setAgendaSmall] = useState(false);
  const [liveVoting, setLiveVoting] = useState<SingleVoting | null>(null);
  const [liveParticipants, setLiveParticipants] = useState<
    LiveParticipantType[]
  >([]);
  const [remoteScreenShareEnabled, setRemoteScreenShareEnabled] =
    useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const apiKey = process.env.REACT_APP_VONAGE_API_KEY;
  const [failedMessages, setFailedMessages] = useState<RetryChatMessage[]>([]);

  useEffect(() => {
    setSubscribersCount(subscribers.length);
  }, [subscribers]);

  //Remote screen share handling
  useEffect(() => {
    const isSharing = subscribers.some(
      (subscriber) => subscriber.stream?.videoType === "screen"
    );
    setRemoteScreenShareEnabled(isSharing);

    //If someone is sharing screen, we are finding div with that screen by streamID and
    //moving it to div with id #remote-screen-share-container to style it separately outside of normal grid
    if (isSharing) {
      const screenShareDiv = document.getElementById(
        subscribers.find(
          (subscriber) => subscriber.stream?.videoType === "screen"
        )?.id!
      );
      if (screenShareDiv) {
        //move that div to div with id #remote-screen-share-container
        const remoteScreenShareContainer = document.getElementById(
          "remote-screen-share-container"
        );
        remoteScreenShareContainer &&
          remoteScreenShareContainer.appendChild(screenShareDiv);
      }
    }
  }, [subscribers]);

  const onStreamCreated = useCallback(
    (event: { stream: OT.Stream }) => {
      console.log(`Stream ${event.stream.streamId} created`);

      const subscriber = sessionRef.current?.subscribe(
        event.stream,
        "subscriber",
        {
          width: "100%",
          height: "100%",
          showControls: false,
          insertMode: "append",
        },
        (err) => err && console.error(err)
      );

      subscriber && setSubscribers((prev) => [...prev, subscriber]);
    },
    [sessionRef]
  );

  const onStreamDestroyed = useCallback((event: { stream: OT.Stream }) => {
    console.log(`Stream ${event.stream.streamId} destroyed`);

    setSubscribers((prev) =>
      prev.filter(
        (subscriber) => subscriber.stream?.streamId !== event.stream.streamId
      )
    );
  }, []);

  const onConnectionCreated = useCallback(
    (event: { connection: OT.Connection }) => {
      console.log("created connection!", event);

      if (!event.connection.data) {
        console.warn("Connection data is missing!");
      }

      const dataParts = event.connection.data.split("=");

      if (dataParts.length < 2) {
        console.warn("Invalid connection data format:", event.connection.data);
        return;
      }

      const lPdata = {
        connectionId: event.connection.connectionId,
        userId: dataParts[1],
      };

      setLiveParticipants((prev: LiveParticipantType[]) => [
        ...prev.filter((connection) => connection.userId !== lPdata.userId),
        lPdata,
      ]);
    },
    []
  );

  const onConnectionDestroyed = useCallback(
    (event: { connection: OT.Connection }) => {
      console.log("connection destroyed!", event);
      setLiveParticipants((prev: LiveParticipantType[]) => [
        ...prev.filter(
          (user: LiveParticipantType) =>
            user.userId !== event.connection.data.split("=")[1]
        ),
      ]);
    },
    []
  );

  const receiveMessage = useCallback(
    (event: SignalEvent) => {
      console.log("Received message:", event);
      try {
        if (!event.data) {
          console.warn("Received an empty message.");
          return;
        }
        const message = JSON.parse(event.data);
        if (!message?.text) {
          console.warn("Message is empty after trimming.");
          return;
        }
        const senderId = event.from?.connectionId || "unknown";
        const sender = message.sender;
        dispatch(
          addMessage({
            meetingId: message.meetingId,
            message: {
              from: senderId,
              text: message.text,
              timestamp: message.timestamp || Date.now(),
              sender: {
                name: sender?.name || "Unknown",
                id: sender?.id || "unknown",
                email: sender?.email || "unknown",
              },
            },
          })
        );

        // Check if the current user is in the same meeting and is not the sender
        const isInMeeting = liveParticipants.some(
          (participant) => Number(participant.userId) === user?.id
        );
        const isSender = message.sender.id === user?.id;

        // Only show notification if user is in the meeting and is not the sender
        if (isInMeeting && !isSender) {
          toast(`${message.sender.name}: ${message.text}`, {
            autoClose: 3000,
            hideProgressBar: false,
            pauseOnHover: true,
            draggable: true,
            position: "bottom-right",
            className: "meeting-chat-notification",
          });
        }

        // const audio = new Audio('/path/to/notification-sound.mp3');
        // audio.play();
      } catch (error) {
        console.error("Error receiving message:", error);
      }
    },
    [dispatch, liveParticipants, user]
  );

  const connect = useCallback(
    (accessDetails: LiveRoomAccessDetails) => {
      setIsConnecting(true);

      const { sid, access_token } = accessDetails;

      if (apiKey && sid && access_token) {
        sessionRef.current = OT.initSession(apiKey, sid);

        const eventHandlers = {
          sessionConnected: () => {
            console.log("Session Connected!");
            setConnected(true);
          },
          sessionDisconnected: () => {
            console.log("Session Disconnected!");
            setConnected(false);
          },
          connectionCreated: onConnectionCreated,
          connectionDestroyed: onConnectionDestroyed,
          streamCreated: onStreamCreated,
          streamDestroyed: onStreamDestroyed,
          signal: receiveMessage,
        };
        sessionRef.current.on(eventHandlers);

        sessionRef.current.connect(access_token, (err) => {
          if (err) {
            console.error(err);
            setIsConnecting(false);
          } else {
            console.log("Session Connected!");
            setConnected(true);
            setSession(sessionRef.current);
            setIsConnecting(false);
          }
        });
      }
    },
    [
      onStreamCreated,
      onStreamDestroyed,
      apiKey,
      onConnectionCreated,
      onConnectionDestroyed,
      receiveMessage,
    ]
  );

  const disconnect = useCallback(() => {
    if (sessionRef.current) {
      subscribers.forEach((subscriber) => {
        sessionRef.current?.unsubscribe(subscriber);
      });
      sessionRef.current.off();
      sessionRef.current.disconnect();
      sessionRef.current = null;
      setSession(null);
      setConnected(false);
      setSubscribers([]);
      setLiveParticipants([]);
      setSubscribersCount(0);
    }
  }, [
    sessionRef,
    setSession,
    setConnected,
    setSubscribers,
    setSubscribersCount,
    subscribers,
  ]);

  const sendMessage = useCallback(
    (text: string, meetingId: string, session: OT.Session) => {
      if (!session) {
        console.log("No session found");
        return;
      }

      const message = {
        from: session.connection?.connectionId,
        text,
        timestamp: Date.now(),
        meetingId: meetingId,
        sender: {
          name: user?.name || "Unknown",
          id: user?.id || "unknown",
          email: user?.email || "unknown",
        },
      };
      try {
        const attemptSend = (retryCount = 0) => {
          session.signal(
            { type: "chat", data: JSON.stringify(message) },
            (err) => {
              if (err) {
                console.error("Error sending message:", err);
                if (retryCount < 5) {
                  setTimeout(
                    () => attemptSend(retryCount + 1),
                    Math.pow(2, retryCount) * 1000
                  );
                } else {
                  setFailedMessages((prev) => [
                    ...prev,
                    {
                      text,
                      meetingId,
                      session,
                    },
                  ]);
                }
              } else {
                console.log("Message sent successfully");
              }
            }
          );
        };

        attemptSend();
      } catch (error) {
        console.error("Message send failed, adding to retry queue:", error);
        setFailedMessages((prev) => [
          ...prev,
          {
            text,
            meetingId,
            session,
          },
        ]);
      }
    },
    [user, setFailedMessages]
  );

  const retryFailedMessages = useCallback(() => {
    setFailedMessages((prev) => {
      prev.forEach((message) => {
        sendMessage(message.text, message.meetingId, message.session);
      });
      return [];
    });
  }, [sendMessage]);
  return {
    session,
    connected,
    connect,
    disconnect,
    isConnecting,
    subscribers,
    subscribersCount,
    sidebarContent,
    setSidebarContent,
    agendaOpened,
    setAgendaOpened,
    agendaSmall,
    setAgendaSmall,
    liveVoting,
    setLiveVoting,
    remoteScreenShareEnabled,
    liveParticipants,
    sendMessage,
    receiveMessage,
    failedMessages,
    retryFailedMessages,
  };
};

export default useSession;
